import {useState, useEffect, useRef} from "react";
import {BiSolidSend} from "react-icons/bi";
import {ChatHeader} from "./ChatHeader";
import {BotMessage} from "./BotMessage";
import {UserMessage} from "./UserMessage";
import BotApi from "../../apis/ChatBotApi";
import {v4 as uuidv4} from "uuid";
import ChatForm from "./ChatForm";
import {marked} from "marked";
import DOMPurify from "dompurify";
import ChatBotOpen from "../../assets/CHATBOT_OPEN.svg";
import ChatBotClose from "../../assets/CHATBOT_CLOSE.svg";

interface Message {
  id: number;
  texte: string;
  auteur: "user" | "bot" | "form";
}

export function ChatWidget() {
  const [uuid, setUuid] = useState<string>("");
  const [isOpen, setIsOpen] = useState(false);
  const [message, setMessage] = useState("");
  const [messages, setMessages] = useState<Message[]>([]);
  const [isBotTyping, setIsBotTyping] = useState(false);

  const messagesEndRef = useRef<null | HTMLDivElement>(null);

  const BotTyping = () => (
    <div className="flex justify-start">
      <div className="bg-gray-100 text-gray-800 rounded-lg py-3 px-4 rounded-bl-none flex items-center space-x-1">
        <div className="w-2 h-2 bg-black rounded-full animate-bounce [animation-delay:-0.3s]"></div>
        <div className="w-2 h-2 bg-black rounded-full animate-bounce [animation-delay:-0.15s]"></div>
        <div className="w-2 h-2 bg-black rounded-full animate-bounce"></div>
      </div>
    </div>
  );

  const toggleChat = () => {
    setIsOpen(!isOpen);
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({behavior: "smooth"});
  };

  const sleep = (ms: number) =>
    new Promise((resolve) => setTimeout(resolve, ms));

  const handleSendMessage = async () => {
    if (message.trim() === "") {
      return;
    }

    const userMessage: Message = {
      id: Date.now(),
      texte: message,
      auteur: "user",
    };

    setMessages((prevMessages) => [...prevMessages, userMessage]);
    setMessage("");

    if (uuid) {
      try {
        setIsBotTyping(true);
        const apiResponse = await BotApi.getMessage(message, uuid);

        const textSegments = apiResponse.response
          .split("\n\n")
          .filter((segment: string) => segment.trim() !== "");

        const segment = textSegments[0];
        const botMessage: Message = {
          id: Date.now() + 1,
          texte: DOMPurify.sanitize(marked.parse(segment) as string),
          auteur: "bot",
        };
        setMessages((prevMessages) => [...prevMessages, botMessage]);
        setIsBotTyping(false);

        for (let i = 1; i < textSegments.length; i++) {
          const segment = textSegments[i];

          setIsBotTyping(true);

          await sleep(1000);

          setIsBotTyping(false);

          const botMessage: Message = {
            id: Date.now() + i + 1,
            texte: DOMPurify.sanitize(marked.parse(segment) as string),
            auteur: "bot",
          };

          setMessages((prevMessages) => [...prevMessages, botMessage]);
        }

        if (
          apiResponse.response.includes(
            "Je n'arrive pas à vous aider pour le moment."
          ) ||
          apiResponse.response.includes("service client") ||
          apiResponse.response.includes("technique") ||
          apiResponse.response.includes("le support") ||
          apiResponse.response.includes("SAV")
        ) {
          setIsBotTyping(true);
          await sleep(800);
          setIsBotTyping(false);

          setMessages((prevMessages) => [
            ...prevMessages,
            {
              id: Date.now() + 100,
              texte: "",
              auteur: "form",
            },
          ]);
        }
      } catch (error) {
        console.error("Erreur lors de l'appel à l'API :", error);
        setIsBotTyping(false);

        const errorMessage: Message = {
          id: Date.now() + 1,
          texte: "Désolé, une erreur est survenue. Veuillez réessayer.",
          auteur: "bot",
        };
        setMessages((prevMessages) => [...prevMessages, errorMessage]);
      }
    }
  };

  useEffect(() => {
    const botMessage: Message = {
      id: Date.now() + 1,
      texte: "Bonjour ! Comment puis-je vous aider aujourd'hui ?",
      auteur: "bot",
    };
    setMessages((prevMessages) => [...prevMessages, botMessage]);

    let currentUuid = localStorage.getItem("uuid");
    if (!currentUuid) {
      currentUuid = uuidv4();
      localStorage.setItem("uuid", currentUuid);
    }

    setUuid(currentUuid);
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, isBotTyping]);

  const containerClasses = isOpen
    ? "fixed inset-0 z-50 md:inset-auto md:bottom-20 md:right-5"
    : "fixed bottom-5 right-5 z-50 md:bottom-20";

  return (
    <div className={containerClasses}>
      {isOpen && (
        <div className="w-full h-full md:w-[400px] md:h-[600px] flex flex-col bg-white md:rounded-lg shadow-xl border border-gray-200">
          <ChatHeader onClose={toggleChat} />

          <div className="flex-1 p-4 overflow-y-auto space-y-3 bg-white">
            {messages.map((msg) => {
              if (msg.auteur === "form") {
                return <ChatForm key={msg.id} messages={messages} />;
              }

              if (msg.auteur === "user") {
                return <UserMessage key={msg.id} message={msg.texte} />;
              }

              return <BotMessage key={msg.id} message={msg.texte} />;
            })}

            {isBotTyping && <BotTyping />}

            <div ref={messagesEndRef} />
          </div>

          <div className="p-4 border-t border-gray-200 bg-gray-50 md:rounded-b-lg pb-safe">
            <div className="flex items-center space-x-2">
              <input
                value={message}
                type="text"
                placeholder="Écrivez votre message..."
                className="flex-1 border border-gray-300 rounded-full py-2 px-4 focus:outline-none focus:border-blue-500"
                onChange={(e) => setMessage(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    handleSendMessage();
                  }
                }}
              />
              <button
                className="text-white rounded-full p-2 hover:opacity-90 transition-opacity"
                onClick={handleSendMessage}>
                <BiSolidSend size={20} color="#405dae" />
              </button>
            </div>
          </div>
        </div>
      )}

      {!isOpen && (
        <button
          onClick={toggleChat}
          aria-label="Ouvrir le chat"
          className="group">
          <img
            src={ChatBotClose}
            alt="Logo ChatBot"
            className="h-16 md:h-20 object-cover block group-hover:hidden"
          />
          <img
            src={ChatBotOpen}
            alt="Logo ChatBot Hover"
            className="h-16 md:h-20 object-cover hidden group-hover:block"
          />
        </button>
      )}
    </div>
  );
}
